import { useContext, useState } from 'react';

import { CheckOutlined, DownOutlined, EditOutlined, ExclamationOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';

import { cn } from '@/lib/cn';
import useProductStore from '@/stores/productStore';

import { FormGroupContext } from './FormGroup';

export const Block = ({
  children,
  isValid = true,
  isEditing = false,
  lengthIndicator = undefined,
  title,
  subtitle = undefined,
  reviewWarning = false,
  warningTooltip = undefined,
  allowOverflow = false,
  allowEdit = true,
  name,
  onEdit
}: {
  children: React.ReactNode;
  isValid?: boolean;
  isEditing: boolean;
  lengthIndicator?: string;
  title: string;
  subtitle?: string;
  reviewWarning?: boolean;
  warningTooltip?: string;
  allowOverflow?: boolean;
  allowEdit?: boolean;
  name: string;
  onEdit?: (editing: boolean) => void;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { collapsable, showValidationState, reviewMode, allowEdit: contextAllowEdit } = useContext(FormGroupContext);
  const iaGenerated = useProductStore(p => p.iaGenerated);

  return (
    <div
      className={cn(
        'bg-surface-disabled dark:bg-surface-subtle border-outline-disabled flex-1 rounded-lg border p-4 shadow-sm',
        {
          'border-ai-with-bg-surface-subtle': iaGenerated[name]
        }
      )}
    >
      <div className='flex flex-row items-center gap-2'>
        {showValidationState && !reviewMode && (
          <>
            {!isValid && (
              <div className='flex h-4 w-4 items-center justify-center rounded-full border border-gray-500'></div>
            )}
            {isValid && (
              <div className='flex h-4 w-4 items-center justify-center rounded-full border border-black bg-black font-bold text-white'>
                <CheckOutlined className='h-3 w-3' />
              </div>
            )}
          </>
        )}

        <div className='flex flex-1 flex-col'>
          <div className='flex flex-row'>
            <div className='flex-1 text-base font-semibold'>{title}</div>
            {lengthIndicator && <div className='text-sm text-gray-400'>{lengthIndicator}</div>}
          </div>
          {subtitle && <div className='text-sm'>{subtitle}</div>}
        </div>
        {collapsable && !reviewMode && (
          <div
            onClick={() => setIsCollapsed(c => !c)}
            className='inline-flex cursor-pointer items-center justify-center'
            id={`form-block-${name}-collapse-button`}
          >
            <DownOutlined
              className={cn('transition-transform duration-200 ease-in-out', isCollapsed ? 'rotate-0' : 'rotate-180')}
            />
          </div>
        )}
        {reviewMode && (
          <>
            {reviewWarning && (
              <Tooltip title={warningTooltip} trigger={'hover'}>
                <div className='flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-white'>
                  <ExclamationOutlined className='' />
                </div>
              </Tooltip>
            )}
            <div
              onClick={() => {
                onEdit?.(!isEditing);
              }}
              id={`form-block-${name}-edit-button`}
              className='inline-flex cursor-pointer items-center justify-center'
            >
              {allowEdit && contextAllowEdit && !isEditing && (
                <EditOutlined className={cn('transition-transform duration-200 ease-in-out')} />
              )}
            </div>
          </>
        )}
      </div>
      <div
        className={cn('flex flex-col gap-2 overflow-hidden transition-all duration-200', {
          'mt-0 max-h-0 scale-y-0 opacity-0 ease-out': isCollapsed,
          'mt-2 opacity-100 ease-in': !isCollapsed,
          'overflow-visible': allowOverflow,
          'text-content-body text-sm leading-5 whitespace-pre-wrap': reviewMode
        })}
      >
        {children}
      </div>
    </div>
  );
};
